import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../types/index';
import adminApiService from '../services/adminApi';
import authService, { type AuthError, type LoginCredentials } from '../services/authService';
import toast from 'react-hot-toast';

// Types
type Theme = 'light' | 'dark';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
}

interface AppState {
  auth: AuthState;
  theme: Theme;
  sidebarCollapsed: boolean;
  globalSettings: Record<string, unknown> | null;
}

type AppAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: AuthError }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_AUTH_ERROR' }
  | { type: 'SET_THEME'; payload: Theme }
  | { type: 'TOGGLE_THEME' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'SET_GLOBAL_SETTINGS'; payload: Record<string, unknown> };

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Auth methods
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (userData: {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
  }) => Promise<void>;
  verifyOTP: (email: string, otp: string) => Promise<boolean>;
  resendOTP: (email: string) => Promise<void>;
  clearAuthError: () => void;
  // App methods
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  loadGlobalSettings: () => Promise<void>;
}

// Helper function to check if a session may exist
// With HTTP-only cookies, we can't read the actual tokens from JavaScript
// Instead, we check for the stored user data which indicates a previous login
const hasStoredSession = (): boolean => {
  try {
    // Check for stored user - indicates a previous successful login
    const storedUser = sessionStorage.getItem('auth_user');
    return !!storedUser;
  } catch (e) {
    console.warn('Failed to check stored session:', e);
    return false;
  }
};

// Initial state
const initialState: AppState = {
  auth: {
    user: null,
    isAuthenticated: false,
    isLoading: hasStoredSession(), // Set loading to true if a session may exist
    error: null,
  },
  theme: (localStorage.getItem('gp_admin_theme') as Theme) || 'light',
  sidebarCollapsed: localStorage.getItem('gp_admin_sidebar_collapsed') === 'true',
  globalSettings: null,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        auth: {
          ...state.auth,
          isLoading: true,
          error: null,
        },
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        auth: {
          user: action.payload,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        },
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        auth: {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: action.payload,
        },
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        auth: {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        },
      };
    case 'CLEAR_AUTH_ERROR':
      return {
        ...state,
        auth: {
          ...state.auth,
          isLoading: false, // Ensure loading is cleared when clearing errors
          error: null,
        },
      };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.payload };
    case 'SET_GLOBAL_SETTINGS':
      return { ...state, globalSettings: action.payload };
    default:
      return state;
  }
}

// Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  readonly children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Debug log to verify AppProvider is rendering


  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (state.theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    localStorage.setItem('gp_admin_theme', state.theme);
  }, [state.theme]);

  // Apply global settings CSS variables
  useEffect(() => {
    if (state.globalSettings?.attributes && typeof state.globalSettings.attributes === 'object') {
      const root = document.documentElement;
      const attributes = state.globalSettings.attributes as Record<string, string>;
      
      if (attributes.primaryColor) {
        root.style.setProperty('--primary-color', attributes.primaryColor);
      }
      if (attributes.secondaryColor) {
        root.style.setProperty('--secondary-color', attributes.secondaryColor);
      }
      if (attributes.accentColor) {
        root.style.setProperty('--accent-color', attributes.accentColor);
      }
    }
  }, [state.globalSettings]);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('gp_admin_sidebar_collapsed', state.sidebarCollapsed.toString());
  }, [state.sidebarCollapsed]);

  // Initialize authentication state
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      dispatch({ type: 'AUTH_START' });
      
      try {
        const user = await authService.initializeAuth();

        if (isMounted) {
          if (user) {
            dispatch({ type: 'AUTH_SUCCESS', payload: user });
          } else {
            // No user returned - check if we had a stored session
            const storedUser = sessionStorage.getItem('auth_user');
            if (storedUser) {
              // Had stored session but it failed - this is a logout scenario
              // Clear the invalid stored session
              sessionStorage.removeItem('auth_user');
              dispatch({ type: 'AUTH_LOGOUT' });
            } else {
              // No stored session found - clear loading state without logout
              dispatch({ type: 'CLEAR_AUTH_ERROR' });
            }
          }
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        if (isMounted) {
          // Always clear loading state, but handle error appropriately
          const storedUser = sessionStorage.getItem('auth_user');
          if (storedUser) {
            // Had stored session but got error - this is an auth failure
            // Clear the invalid stored session
            sessionStorage.removeItem('auth_user');
            dispatch({ 
              type: 'AUTH_FAILURE', 
              payload: error as AuthError 
            });
          } else {
            // No stored session and got error - clear loading state without failure
            dispatch({ type: 'CLEAR_AUTH_ERROR' });
          }
        }
      }
    };

    // Add a small delay to ensure localStorage is ready
    const timer = setTimeout(() => {
      initializeAuth();
    }, 100);

    // Listen for auth events
    const handleAuthLogout = () => {
      if (isMounted) {
        dispatch({ type: 'AUTH_LOGOUT' });
        toast.error('Session expired. Please login again.');
      }
    };

    globalThis.addEventListener('auth:logout', handleAuthLogout);

    return () => {
      clearTimeout(timer);
      isMounted = false;
      globalThis.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  // Auth methods
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await authService.login(credentials);
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      toast.success('Login successful!');
      return true;
    } catch (error) {
      const authError = error as AuthError;
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      toast.error(authError.message);
      return false;
    }
  };

  const register = async (userData: {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
  }): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      await authService.register(userData);
      toast.success('Registration successful! Please check your email to verify your account.');
    } catch (error) {
      const authError = error as AuthError;
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      toast.error(authError.message);
      throw error;
    }
  }

  const verifyOTP = async (email: string, otp: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const result = await authService.verifyOTP(email, otp);
      if (result) {
        toast.success('Account verified successfully! You can now login.');
      }
      return result;
    } catch (error) {
      const authError = error as AuthError;
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      toast.error(authError.message);
      return false;
    }
  };

  const resendOTP = async (email: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      await authService.resendOTP(email);
      toast.success('OTP resent successfully! Check your email.');
    } catch (error) {
      const authError = error as AuthError;
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      toast.error(authError.message);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear auth state even if API call fails
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const clearAuthError = (): void => {
    dispatch({ type: 'CLEAR_AUTH_ERROR' });
  };

  // App methods
  const setTheme = (theme: Theme): void => {
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  const toggleSidebar = (): void => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  };

  const setSidebarCollapsed = (collapsed: boolean): void => {
    dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
  };

  const loadGlobalSettings = async (): Promise<void> => {
    try {
      const settings = await adminApiService.getGlobal();
      dispatch({ type: 'SET_GLOBAL_SETTINGS', payload: settings });
    } catch (error) {
      toast.error(`Failed to load global settings: ${error}`);
    }
  };

  const contextValue = useMemo<AppContextType>(() => ({
    state,
    dispatch,
    login,
    logout,
    register,
    verifyOTP,
    resendOTP,
    clearAuthError,
    setTheme,
    toggleSidebar,
    setSidebarCollapsed,
    loadGlobalSettings,
  }), [state]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

// Hook to use the app context
export function useApp(): AppContextType {
  const context = useContext(AppContext);
  
  // Debug log to check context availability

  
  if (context === undefined) {
    console.error('❌ useApp called outside of AppProvider');
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Convenience hook for auth-specific functionality
export function useAuth() {
  const { state, login, logout, register, verifyOTP, resendOTP, clearAuthError } = useApp();
  return {
    user: state.auth.user,
    isAuthenticated: state.auth.isAuthenticated,
    isLoading: state.auth.isLoading,
    error: state.auth.error,
    login,
    logout,
    register,
    verifyOTP,
    resendOTP,
    clearAuthError,
  };
}